/**
 * Factory + custom AuditEvent viewer for Unify Admin / Unify Assistant (ENG-3351).
 * Full field catalog via Columns picker; sensitive fields opt-in (hidden by default).
 */
import { Alert, Button, Container, Group, Select, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useMedplum } from '@medplum/react-hooks';
import type { Bot } from '@medplum/fhirtypes';
import { IconLayoutColumns } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { DateRangeSelect } from '@/components/DateRangeSelect';
import { TablePaginationFooter } from '@/components/TablePaginationFooter';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { usePermissions } from '@/contexts/PermissionContext';
import { useMedplumDirectoryUsers } from '@/hooks/useMedplumDirectoryUsers';
import {
  dateInputValueToDateOnly,
  formatUsDateTimeResolved,
  resolveDisplayTimeZone,
  type CalendarDateRange,
} from '@/lib/dateHelpers';
import { ColumnSelectModal } from '@/pages/episode-tracker/components/ColumnSelectModal';
import {
  AUDIT_LOG_COLUMN_CATALOG,
  AUDIT_LOG_DEFAULT_COLUMN_KEYS,
  auditLogColumnOptions,
  loadAuditLogColumnPreference,
  saveAuditLogColumnPreference,
  truncateAuditLogCell,
} from './auditLogColumns';
import { canAccessAuditLogsUi } from './auditLogsAccess';
import {
  AUDIT_EVENT_PAGE_SIZE,
  type AuditEventActionFilter,
} from './buildAuditEventSearchParams';
import { fetchAllBots } from './fetchAllBots';
import { fetchAuditEventPage } from './fetchAuditEventPage';
import { OUTCOME_LABELS, type AuditLogRow } from './mapAuditEventToRow';
import {
  buildBotNameByReference,
  buildDirectoryNameByReference,
  mergeNameByReferenceMaps,
  resolveAuditLogRowNames,
} from './resolveAuditLogRowNames';
import { userToAgentReference } from './userToAgentReference';

const ACTION_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'C', label: 'Create' },
  { value: 'R', label: 'Read' },
  { value: 'U', label: 'Update' },
  { value: 'D', label: 'Delete' },
  { value: 'E', label: 'Execute' },
];

function rangeToDateFilters(range: CalendarDateRange): { dateFrom: string; dateTo: string } {
  const [from, to] = range;
  return {
    dateFrom: from ? dateInputValueToDateOnly(from) : '',
    dateTo: to ? dateInputValueToDateOnly(to) : '',
  };
}

function renderTruncatedCell(value: string): ReactNode {
  const { display, full } = truncateAuditLogCell(value);
  return (
    <Text size="sm" title={full} aria-label={full} style={{ wordBreak: 'break-word' }}>
      {display}
    </Text>
  );
}

export function AuditLogsPage(): JSX.Element {
  const medplum = useMedplum();
  const rbac = usePermissions();
  const { users, loading: usersLoading } = useMedplumDirectoryUsers();
  const eligible = canAccessAuditLogsUi(rbac);
  const requestGenRef = useRef(0);

  const [dateRange, setDateRange] = useState<CalendarDateRange>([null, null]);
  const [action, setAction] = useState<AuditEventActionFilter>('');
  const [agent, setAgent] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(String(AUDIT_EVENT_PAGE_SIZE));
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [bots, setBots] = useState<Bot[]>([]);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [visibleColumnOrder, setVisibleColumnOrder] = useState<string[] | null>(() =>
    loadAuditLogColumnPreference(),
  );

  const { dateFrom, dateTo } = useMemo(() => rangeToDateFilters(dateRange), [dateRange]);
  const pageSizeNum = parseInt(pageSize, 10) || AUDIT_EVENT_PAGE_SIZE;

  const botNameByRef = useMemo(() => buildBotNameByReference(bots), [bots]);

  const nameByRef = useMemo(
    () => mergeNameByReferenceMaps(buildDirectoryNameByReference(users), botNameByRef),
    [users, botNameByRef],
  );

  const agentOptions = useMemo(() => {
    const userOpts = [...users]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((user) => ({
        value: userToAgentReference(user),
        label: user.email ? `${user.name} (${user.email})` : user.name,
      }));
    const botOpts = [...bots]
      .filter((bot) => bot.id && bot.name?.trim())
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map((bot) => ({
        value: `Bot/${bot.id}`,
        label: `${bot.name!.trim()} (Bot)`,
      }));
    return [...userOpts, ...botOpts];
  }, [users, bots]);

  const columnOptions = useMemo(() => auditLogColumnOptions(), []);

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    (async () => {
      try {
        medplum.invalidateSearches('Bot');
        const fetched = await fetchAllBots(medplum);
        if (cancelled) return;
        setBots(fetched);
      } catch {
        if (!cancelled) setBots([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eligible, medplum, reloadKey]);

  const filterKey = useMemo(
    () => `${dateFrom}|${dateTo}|${action}|${agent}|${page}|${pageSize}|${reloadKey}`,
    [dateFrom, dateTo, action, agent, page, pageSize, reloadKey],
  );

  const resetToFirstPage = useCallback(() => {
    setPage(1);
  }, []);

  const loadPage = useCallback(async (): Promise<void> => {
    if (!eligible) return;
    const gen = ++requestGenRef.current;
    setLoading(true);
    setError(null);
    medplum.invalidateSearches('AuditEvent');
    try {
      const result = await fetchAuditEventPage(medplum, {
        dateFrom,
        dateTo,
        action,
        agent,
        offset: (page - 1) * pageSizeNum,
        count: pageSizeNum,
      });
      if (gen !== requestGenRef.current) return;

      // If filters/total shrank and we landed past the last page, clamp back.
      if (result.rows.length === 0 && page > 1 && result.total > 0) {
        const lastPage = Math.max(1, Math.ceil(result.total / pageSizeNum));
        setPage(Math.min(page - 1, lastPage));
        return;
      }
      if (result.rows.length === 0 && page > 1 && result.total === 0) {
        setPage(1);
        return;
      }

      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      if (gen !== requestGenRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load audit events');
    } finally {
      if (gen === requestGenRef.current) setLoading(false);
    }
  }, [eligible, medplum, dateFrom, dateTo, action, agent, page, pageSizeNum]);

  useEffect(() => {
    void loadPage();
  }, [eligible, filterKey, loadPage]);

  const displayRows = useMemo(
    () => rows.map((row) => resolveAuditLogRowNames(row, nameByRef)),
    [rows, nameByRef],
  );

  const columns = useMemo((): UnifyTableColumn<AuditLogRow>[] => {
    const headerWithTooltip = (label: string, tooltip: string) => (
      <Tooltip label={tooltip} multiline maw={280} withArrow>
        <Text span size="sm" fw={500} style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>
          {label}
        </Text>
      </Tooltip>
    );

    return AUDIT_LOG_COLUMN_CATALOG.map((def) => {
      const base: UnifyTableColumn<AuditLogRow> = {
        key: def.key,
        label: def.label,
        getValue: (row) => row[def.key] ?? '',
        render: (row) => renderTruncatedCell(row[def.key] ?? '—'),
      };

      if (def.key === 'recorded') {
        return {
          ...base,
          render: (row) =>
            row.recorded
              ? formatUsDateTimeResolved(row.recorded, { ...resolveDisplayTimeZone(), seconds: true })
              : '—',
        };
      }

      if (def.key === 'outcome') {
        return {
          ...base,
          renderHeader: () => (
            <Tooltip
              label={
                <Stack gap={2}>
                  {Object.entries(OUTCOME_LABELS).map(([code, label]) => (
                    <Text key={code} size="xs">
                      {code} — {label}
                    </Text>
                  ))}
                </Stack>
              }
              multiline
              withArrow
            >
              <Text span size="sm" fw={500} style={{ cursor: 'help', borderBottom: '1px dotted currentColor' }}>
                Outcome
              </Text>
            </Tooltip>
          ),
          render: (row) => row.outcome || '—',
        };
      }

      if (def.key === 'agentDisplay') {
        return {
          ...base,
          renderHeader: () =>
            headerWithTooltip(
              'Agent',
              'Who performed the action — a directory user or bot name when known, otherwise a FHIR reference.',
            ),
        };
      }

      if (def.key === 'entitySummary') {
        return {
          ...base,
          renderHeader: () =>
            headerWithTooltip(
              'Entity',
              'What was acted on (FHIR references by default). Use Columns to show entity name, query, and other detail fields.',
            ),
        };
      }

      return base;
    });
  }, []);

  const visibleColumnKeys = visibleColumnOrder ?? AUDIT_LOG_DEFAULT_COLUMN_KEYS;

  if (!rbac.ready) {
    return (
      <Container size="xl" py="xl">
        <Text c="dimmed">Loading permissions…</Text>
      </Container>
    );
  }

  if (!eligible) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Audit Logs</Title>
          <Text c="dimmed" size="sm" mt={4}>
            Factory and custom Medplum AuditEvents. Choose columns to include sensitive detail.
            Older rows may lack detail if saved while redaction was on.
          </Text>
        </div>

        <Group align="flex-end" gap="md" wrap="wrap">
          <DateRangeSelect
            label="Date range"
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
              resetToFirstPage();
            }}
            placeholder="Select date range"
            size="sm"
            w={280}
            clearable
          />
          <Select
            label="Event"
            data={ACTION_OPTIONS}
            value={action}
            onChange={(v) => {
              setAction((v ?? '') as AuditEventActionFilter);
              resetToFirstPage();
            }}
            size="sm"
            w={160}
            clearable={false}
          />
          <Select
            label="Agent"
            placeholder="All agents"
            data={agentOptions}
            value={agent || null}
            onChange={(v) => {
              setAgent(v ?? '');
              resetToFirstPage();
            }}
            searchable
            clearable
            disabled={usersLoading}
            nothingFoundMessage="No agents found"
            size="sm"
            w={280}
          />
          <Button
            variant="default"
            size="sm"
            leftSection={<IconLayoutColumns size={16} />}
            onClick={() => setColumnModalOpen(true)}
          >
            Columns
          </Button>
          <Button
            variant="light"
            color="gray"
            size="sm"
            loading={loading}
            onClick={() => {
              resetToFirstPage();
              setReloadKey((k) => k + 1);
            }}
          >
            Refresh
          </Button>
        </Group>

        {error ? (
          <Alert color="red" title="Could not load audit events">
            {error}
          </Alert>
        ) : null}

        <Stack gap="sm" style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : undefined }}>
          <UnifyTable
            columns={columns}
            visibleColumnKeys={visibleColumnKeys}
            data={displayRows}
            getRowKey={(row) => row.id}
            emptyMessage={loading ? 'Loading audit events…' : 'No audit events found for these filters.'}
          />
          <TablePaginationFooter
            totalItems={total}
            currentPage={page}
            onPageChange={setPage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(value) => {
              if (value != null) setPageSize(value);
            }}
            aria-label="Audit logs pagination"
          />
        </Stack>
      </Stack>

      <ColumnSelectModal
        opened={columnModalOpen}
        onClose={() => setColumnModalOpen(false)}
        allColumns={columnOptions}
        defaultColumnKeys={AUDIT_LOG_DEFAULT_COLUMN_KEYS}
        value={visibleColumnOrder}
        onApply={(next) => {
          setVisibleColumnOrder(next);
          saveAuditLogColumnPreference(next);
        }}
      />
    </Container>
  );
}
